// Navica Supports brand marks, self-contained SVG components (no external assets).
// The 4-petal flower is the primary mark; the circular seal wraps "Always with you · Never above you".

/**
 * Four-petal Navica flower mark. Coral petals (top-right, bottom-left) crossed
 * with orange petals (top-left, bottom-right), evoking the four flags/streams
 * of practice held together at the centre.
 */
export function NavicaMark({ size = 40, className = "", title = "Navica Supports" }) {
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 100 100"
      className={className}
      role="img" aria-label={title}
      xmlns="http://www.w3.org/2000/svg"
    >
      <title>{title}</title>
      <defs>
        <linearGradient id="navica-coral" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F0879E" />
          <stop offset="1" stopColor="#C25971" />
        </linearGradient>
        <linearGradient id="navica-orange" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F09A5B" />
          <stop offset="1" stopColor="#D46F27" />
        </linearGradient>
      </defs>

      {/* Group rotated so petals meet at 50,50 */}
      <g transform="translate(50 50)">
        {/* Top-right coral petal */}
        <path d="M0,0 C 22,-6 34,-22 30,-40 C 12,-38 -4,-24 0,0 Z"
              fill="url(#navica-coral)" transform="rotate(0)" />
        {/* Bottom-right orange petal */}
        <path d="M0,0 C 22,-6 34,-22 30,-40 C 12,-38 -4,-24 0,0 Z"
              fill="url(#navica-orange)" transform="rotate(90)" />
        {/* Bottom-left coral petal */}
        <path d="M0,0 C 22,-6 34,-22 30,-40 C 12,-38 -4,-24 0,0 Z"
              fill="url(#navica-coral)" transform="rotate(180)" />
        {/* Top-left orange petal */}
        <path d="M0,0 C 22,-6 34,-22 30,-40 C 12,-38 -4,-24 0,0 Z"
              fill="url(#navica-orange)" transform="rotate(270)" />
      </g>
    </svg>
  );
}

/**
 * Circular seal, "ALWAYS WITH YOU · NEVER ABOVE YOU". Two-tone coral: peach on the
 * top arc, coral on the bottom, with vertical bar dividers at 9 & 3 o'clock. No
 * centre mark, the ring itself is the emblem.
 */
export function NavicaSeal({ size = 200, className = "" }) {
  const peach = "#F0A480";        // top arc
  const coral = "#E2718A";        // bottom arc
  return (
    <svg
      width={size} height={size}
      viewBox="0 0 200 200"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Always with you, never above you"
    >
      <defs>
        {/* Top arc, text sits ON the arc so its baseline hugs the curve.
            Radius 78 keeps the letters inside the SVG box comfortably. */}
        <path id="seal-top" d="M 22,100 A 78,78 0 0 1 178,100" fill="none" />
        {/* Bottom arc, anti-clockwise so text reads left-to-right on the bottom. */}
        <path id="seal-bottom" d="M 22,100 A 78,78 0 0 0 178,100" fill="none" />
      </defs>

      {/* Top arc text, peachy salmon */}
      <text
        fill={peach}
        style={{
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: "20px",
          fontWeight: 800,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
        }}
      >
        <textPath href="#seal-top" startOffset="50%" textAnchor="middle">
          ALWAYS WITH YOU
        </textPath>
      </text>

      {/* Bottom arc text, coral pink */}
      <text
        fill={coral}
        style={{
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: "20px",
          fontWeight: 800,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
        }}
      >
        <textPath href="#seal-bottom" startOffset="50%" textAnchor="middle">
          NEVER ABOVE YOU
        </textPath>
      </text>

      {/* Vertical bar dividers at 9 & 3 o'clock */}
      <rect x="10"  y="93" width="4" height="14" rx="1.5" fill={peach} />
      <rect x="186" y="93" width="4" height="14" rx="1.5" fill={peach} />
    </svg>
  );
}

/**
 * Cultural safety flags strip, Aboriginal, Torres Strait Islander, Progress Pride.
 * Rendered as inline SVGs so we don't depend on any external images.
 */
export function CulturalFlagsStrip({ className = "", showLabels = true }) {
  return (
    <div className={`flex items-center gap-3 flex-wrap ${className}`} role="group" aria-label="Cultural safety commitments">
      <FlagAboriginal />
      <FlagTorresStrait />
      <FlagProgressPride />
      {showLabels && (
        <div className="text-xs text-[#6B7A80] leading-tight ml-1">
          <div>We acknowledge First Nations peoples as the</div>
          <div>traditional custodians. LGBTQIA+ safe.</div>
        </div>
      )}
    </div>
  );
}

function FlagAboriginal() {
  return (
    <svg width="36" height="24" viewBox="0 0 36 24" role="img" aria-label="Aboriginal flag" className="rounded-sm border border-[#F0E5DE]">
      <rect x="0" y="0"  width="36" height="12" fill="#000000" />
      <rect x="0" y="12" width="36" height="12" fill="#C42525" />
      <circle cx="18" cy="12" r="5" fill="#FFC61E" />
    </svg>
  );
}

function FlagTorresStrait() {
  return (
    <svg width="36" height="24" viewBox="0 0 36 24" role="img" aria-label="Torres Strait Islander flag" className="rounded-sm border border-[#F0E5DE]">
      <rect x="0" y="0" width="36" height="24" fill="#0B833B" />
      <rect x="0" y="4" width="36" height="16" fill="#003580" />
      <rect x="0" y="7" width="36" height="10" fill="#FFFFFF" />
      {/* Dhari headdress (simplified) */}
      <path d="M18 10 L14 14 L18 12 L22 14 Z" fill="#FFFFFF" stroke="#0B833B" strokeWidth="0.5" />
      {/* Five-pointed star */}
      <polygon points="18,10 18.7,11.6 20.4,11.7 19.1,12.7 19.5,14.3 18,13.4 16.5,14.3 16.9,12.7 15.6,11.7 17.3,11.6" fill="#FFFFFF" />
    </svg>
  );
}

function FlagProgressPride() {
  return (
    <svg width="36" height="24" viewBox="0 0 36 24" role="img" aria-label="Progress Pride flag" className="rounded-sm border border-[#F0E5DE]">
      {/* Rainbow bands */}
      <rect x="8" y="0"  width="28" height="4" fill="#E40303" />
      <rect x="8" y="4"  width="28" height="4" fill="#FF8C00" />
      <rect x="8" y="8"  width="28" height="4" fill="#FFED00" />
      <rect x="8" y="12" width="28" height="4" fill="#008026" />
      <rect x="8" y="16" width="28" height="4" fill="#24408E" />
      <rect x="8" y="20" width="28" height="4" fill="#732982" />
      {/* Progress chevron */}
      <polygon points="0,0 8,0 20,12 8,24 0,24" fill="#FFFFFF" />
      <polygon points="0,0 5,0 17,12 5,24 0,24" fill="#F5A9B8" />
      <polygon points="0,0 3,0 15,12 3,24 0,24" fill="#5BCEFA" />
      <polygon points="0,0 1.5,0 13.5,12 1.5,24 0,24" fill="#603813" />
      <polygon points="0,0 0.5,0 12.5,12 0.5,24 0,24" fill="#000000" />
    </svg>
  );
}

/** Small "We ❤ NDIS" registered/aligned provider badge, used in trust/footer. */
export function WeLoveNdisBadge({ className = "" }) {
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#4B2C8A] text-white text-xs font-semibold ${className}`}>
      <span>We</span>
      <svg width="14" height="14" viewBox="0 0 20 20" fill="#FF5B7A"><path d="M10 17s-6-4-6-9a3.5 3.5 0 0 1 6-2.5A3.5 3.5 0 0 1 16 8c0 5-6 9-6 9z" /></svg>
      <span className="lowercase tracking-wider">ndis</span>
    </div>
  );
}
